/**
 * Add-to-watchlist bottom sheet presentation.
 *
 * Composed on the shared MobileSheetChrome primitive (SE-5525), which owns
 * the sheet surface, ✕ close chip, height cap, and every dismissal path
 * (backdrop, swipe-down, ESC, Android back). The bespoke 40%-window-height
 * inline-style cap and PanGestureHandler this view used to carry are gone —
 * the chrome's capped, scrollable body replaces both. The "Done" link lives
 * in the chrome's sticky header band via `headerContent`.
 * @module components/organisms/AddToWatchlistActionsheet
 */

import { PlusIcon } from "lucide-react-native";
import React, { memo } from "react";

import CustomPromptContainer from "@/components/molecules/CustomPrompt";
import { CustomPromptRefProps } from "@/components/molecules/CustomPrompt/CustomPromptContainer";
import MobileSheetChrome from "@/components/molecules/MobileSheetChrome";
import WatchlistAddRemoveButton from "@/components/molecules/WatchlistAddRemoveButton";

import { Box } from "@/components/atoms/Box";
import { Button } from "@/components/atoms/Button";
import { Divider } from "@/components/atoms/Divider";
import { Icon } from "@/components/atoms/Icon";
import { Pressable } from "@/components/atoms/Pressable";
import { Text } from "@/components/atoms/Text";
import { Stack } from "@/components/atoms/Stack";
import {
  PlayerSearchPlayerFragment,
  WatchlistWatchlistMetaFragment,
} from "@/generated/graphql";
import { PlayerCardData } from "@/types/playerCardTypes";

/**
 * Player accepted by the add-to-watchlist actionsheet view.
 * @remarks Mirrors the container's accepted shape — either a full search-result
 *   player or the lightweight roster/watchlist card node (PlayerCardData), since
 *   the actionsheet only reads identity fields (SE-5377). `isManual` is optional.
 */
type ActionsheetPlayerItem =
  | (Omit<PlayerSearchPlayerFragment, "isManual"> & {
      isManual?: boolean;
    })
  | PlayerCardData;

/**
 * Props for AddToWatchlistActionsheetView
 */
interface Props {
  /** Player data to add to watchlist */
  item: ActionsheetPlayerItem;
  /** Whether the actionsheet is currently visible */
  showActionsheet: boolean;
  /** Callback invoked when actionsheet is closed */
  onClose: () => void;
  /** Callback invoked when add button is pressed */
  onAddPress: () => void;
  /** Array of available watchlists to display */
  watchlists: WatchlistWatchlistMetaFragment[];
  /** Callback invoked when creating a new watchlist */
  onAddWatchlistPress: () => void;
  /** Whether watchlist operations are in progress */
  loading: boolean;
  /** Whether the player is already in any watchlist */
  isPlayerInAnyWatchlist: boolean;
  /** Optional callback invoked when player is successfully added to watchlist */
  onAddPlayerToWatchlistSuccess?: () => void;
  /** Optional ref to the custom prompt component for user confirmations */
  promptRef?: React.RefObject<CustomPromptRefProps>;
  /** Whether screen width is below mobile breakpoint */
  isSmallScreen: boolean;
  /** Whether dark theme is active */
  isDark: boolean;
}

/**
 * Displays actionsheet for adding a player to watchlists
 * @param root0 - Component props
 * @param root0.item - Player data to add to watchlist
 * @param root0.showActionsheet - Whether the actionsheet is currently visible
 * @param root0.onClose - Callback invoked when actionsheet is closed
 * @param root0.onAddPress - Callback invoked when add button is pressed
 * @param root0.watchlists - Array of available watchlists to display
 * @param root0.onAddWatchlistPress - Callback invoked when creating a new watchlist
 * @param root0.loading - Whether watchlist operations are in progress
 * @param root0.isPlayerInAnyWatchlist - Whether the player is already in any watchlist
 * @param root0.onAddPlayerToWatchlistSuccess - Optional callback invoked when player is successfully added to watchlist
 * @param root0.promptRef - Optional ref to the custom prompt component for user confirmations
 * @param root0.isSmallScreen - Whether screen width is below mobile breakpoint
 * @param root0.isDark - Whether dark theme is active
 */
const AddToWatchlistActionsheetView = ({
  item,
  showActionsheet,
  onClose,
  onAddPress: _onAddPress,
  watchlists,
  onAddWatchlistPress,
  loading,
  isPlayerInAnyWatchlist: _isPlayerInAnyWatchlist,
  onAddPlayerToWatchlistSuccess,
  promptRef,
  isSmallScreen,
  isDark: _isDark,
}: Props) => (
  <>
    <MobileSheetChrome
      isOpen={showActionsheet}
      onClose={onClose}
      title="Add to List"
      headerContent={
        /* Done — pinned with the title in the chrome's sticky header band */
        <Stack direction="horizontal" className="items-center justify-end">
          <Button
            label="Done"
            variant="link"
            onPress={onClose}
            labelTone="info"
            labelVariant={!isSmallScreen ? "title-sm" : "title"}
          />
        </Stack>
      }
    >
      <Stack className={`w-full ${!isSmallScreen ? "gap-2" : "gap-4"}`}>
        <Text
          variant="title-sm"
          className="pb-4 text-center text-content-secondary"
        >
          Choose which watchlists {item?.name} appears in.
        </Text>

        {/* The chrome's body ScrollView owns scrolling + the height cap; the
         * list keeps only its raised-card container styling. */}
        <Stack className="rounded-md bg-surface-subtle py-4">
          {watchlists?.map(watchlist => (
            <React.Fragment key={watchlist.id}>
              {watchlist.id !== watchlists[0].id && (
                <Box className={!isSmallScreen ? "py-2" : "py-4"}>
                  <Divider className="border-outline-default" />
                </Box>
              )}
              <WatchlistAddRemoveButton
                player={item}
                watchlist={watchlist}
                onAddSuccess={onAddPlayerToWatchlistSuccess}
              />
            </React.Fragment>
          ))}
        </Stack>

        <Pressable
          disabled={loading}
          onPress={onAddWatchlistPress}
          accessibilityRole="button"
          accessibilityLabel="New List"
          className="flex-row items-center justify-center gap-3 rounded-sm bg-surface-subtle p-3"
        >
          <Icon
            as={PlusIcon}
            size={!isSmallScreen ? "md" : "lg"}
            className="text-status-info"
          />
          <Text
            variant={!isSmallScreen ? "body" : "title"}
            className="text-status-info"
          >
            {loading ? "Please Wait..." : "New List"}
          </Text>
        </Pressable>
      </Stack>
    </MobileSheetChrome>
    {/* CustomPrompt is its own overlay (Actionsheet with useRNModal on
     * native / OverlayProvider portal on web), so it presents above the
     * chrome regardless of tree position. */}
    <CustomPromptContainer ref={promptRef} />
  </>
);

AddToWatchlistActionsheetView.displayName = "AddToWatchlistActionsheetView";

export default memo(AddToWatchlistActionsheetView);
